"use client";

import React, { useContext, useRef } from "react";

import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import NavContext from "@/context/NavContext";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useStickyNavigation } from "@/hooks/useStickyNavigation";
import { cn } from "@/utils/general";

const HEADER_HEIGHT_MOBILE = 96;
const HEADER_HEIGHT_DESKTOP = 68;

export type SectionStickyNavItem = {
  text: string;
  id: string;
};

type SectionStickyNavProps = {
  items: SectionStickyNavItem[];
  children: React.ReactNode;
};

export default function SectionStickyNav({ items, children }: SectionStickyNavProps) {
  const navRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const isMobile = useIsMobile(768);
  const { isPageScrolled } = useContext(NavContext);

  const { activeItemId, onItemClick, visible } = useStickyNavigation({
    navRef,
    menuRef,
    padding: isMobile ? 24 : 0,
    offset: isMobile ? HEADER_HEIGHT_MOBILE + 52 : HEADER_HEIGHT_DESKTOP + 54,
  });

  return (
    <div ref={navRef} className="-mt-16">
      <div
        data-visible={visible}
        data-page-scrolled={isPageScrolled}
        className={cn(
          "sticky z-10 transition-[opacity,transform] duration-200 ease-in-out",
          "top-24 md:top-[64px]",
          !visible && "opacity-0",
          isPageScrolled && "sm:-translate-y-6"
        )}
      >
        <div className="w-full mx-auto border-y border-[#cdd2d1] max-w-[1520px] md:w-[calc(100%-48px)] md:border-t-0 md:border-x md:rounded-b-sm">
          <ul
            ref={menuRef}
            className={cn(
              "flex flex-nowrap overflow-x-auto px-6 bg-nova-base-white",
              "shadow-[0px_1.75px_2.31px_0px_#00000002,0px_8px_6.5px_0px_#00000004,0px_20.25px_17.44px_0px_#00000005,0px_40px_40px_0px_#00000008]",
              "scrollbar-none [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
              "sm:justify-center sm:gap-x-6 sm:px-0 md:rounded-b-sm lg:gap-x-11"
            )}
          >
            {items?.map((item, index) => (
              <li
                key={index}
                data-visible={visible}
                className={cn(
                  "transition-opacity duration-500 opacity-0",
                  visible && "opacity-100"
                )}
                style={{ transitionDelay: visible ? `${index * 75 + 200}ms` : "0ms" }}
              >
                <a
                  href={`#${item.id}`}
                  aria-label={item.text}
                  data-active={item.id === activeItemId}
                  onClick={onItemClick(item)}
                  className={cn(
                    "relative block whitespace-nowrap py-[18px] px-3 transition-opacity duration-500 opacity-60",
                    "before:content-[''] before:absolute before:top-[calc(50%-3px)] before:left-0.5 before:w-1 before:h-1 before:rounded-full before:bg-nova-brand-accent-100 before:opacity-0 before:transition-opacity before:duration-500",
                    "hover:opacity-100 hover:[&_p]:bg-nova-base-solid-20",
                    item.id === activeItemId && "opacity-100 before:opacity-100"
                  )}
                >
                  <TextElementNova
                    element="span"
                    className={cn(
                      item.id === activeItemId ? "Body-10-Dia-Strong" : "Body-10-Dia-Normal",
                      "leading-none text-nova-transparency-black-70 px-1"
                    )}
                    text={item.text}
                  />
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div>{children}</div>
    </div>
  );
}
